import React, { useRef } from 'react';
import { Formik, Form as FormikForm } from 'formik';
import clsx from 'clsx';
import { isEmpty } from 'lodash';

import { transformApiErrorToFormik } from '@dashboard/services/form';

import FormElement from '@dashboard/components/Form/components/FormElement';
import Label from '@dashboard/components/Form/components/Label';
import Divider from '@dashboard/components/Divider';

import FormError, { FormErrorBase } from './components/FormError';
import FormSubmit from './components/FormSubmit';
import { SwitchField } from './components/Switch/Switch';
import AutoSave from './components/AutoSave';
import { ColorSelectField } from './components/ColorSelect';
import LexicalRichTextEditor from './components/LexicalRichTextEditor';
import { EmailField } from './components/EmailField';
import ListboxField from './components/Listbox';
import FieldGroup from './components/FieldGroup';
import Disclosure from './components/Disclosure';
import Columns from './components/Columns';
import { ImagesField } from './components/Images';
import OptionList from './components/OptionList';
import TextInput from './components/TextInput';
import Textarea from './components/Textarea';
import Select from './components/Select';
import Checkbox from './components/Checkbox';
import Radio from './components/Radio';
import { ColorPickerField } from './components/ColorPicker';
import CurrencySelector from './components/CurrencySelector';
import { ImageField } from './components/Image';
import PageSelect from './components/PageSelect';
import RadioCard from './components/RadioCard';

function getFieldComponentByType(fieldType) {
  switch (fieldType) {
    case 'divider':
      return Divider;
    case 'fieldgroup':
      return FieldGroup;
    case 'disclosure':
      return Disclosure;
    case 'columns':
      return Columns;
    case 'email':
      return EmailField;
    case 'number':
    case 'url':
    case 'text':
      return TextInput;
    case 'textarea':
      return Textarea;
    case 'rich-text':
      return LexicalRichTextEditor;
    case 'select':
      return Select;
    case 'checkbox':
      return Checkbox;
    case 'radio':
      return Radio;
    case 'color-select':
      return ColorSelectField;
    case 'color':
      return ColorPickerField;
    case 'currency':
      return CurrencySelector;
    case 'image':
      return ImageField;
    case 'images':
      return ImagesField;
    case 'pageSelect':
      return PageSelect;
    case 'radioCard':
      return RadioCard;
    case 'optionList':
      return OptionList;
    case 'toggle':
      return SwitchField;
    case 'listbox':
      return ListboxField;
    default:
      return TextInput;
  }
}

function FormField({ field, inputClassName }) {
  switch (field.type) {
    case 'label': {
      return (
        <Label
          htmlFor={field.id || field.name}
          optional={field.optional && !field.hideOptional}
          tooltip={field.tooltip}
          tags={field.tags}
        >
          {field.label}
        </Label>
      );
    }
    case 'custom': {
      // eslint-disable-next-line no-unused-vars

      return field.renderField(field);
    }
    case 'radioIconCard':
      return (
        <RadioCard.Group
          name={field.name}
          columns={field.columns}
          grid={field.grid}
          className={inputClassName}
        >
          {field.options.map((option, index) => (
            <div
              key={option.value}
              className={clsx({
                'text-center': option.center,
              })}
            >
              <RadioCard.Card
                key={index}
                value={option.value}
                checked={field.value === option.value}
                required={field.required}
                name={field.name}
              >
                {option.component && <option.component />}
              </RadioCard.Card>

              {option.label && (
                <div className="mt-2">
                  <RadioCard.Label>{option.label}</RadioCard.Label>
                </div>
              )}

              {option.labelDescription && (
                <RadioCard.LabelDescription>{option.labelDescription}</RadioCard.LabelDescription>
              )}
            </div>
          ))}
        </RadioCard.Group>
      );
    case 'radioCard':
      return (
        <RadioCard.Group
          name={field.name}
          columns={field.columns}
          grid={field.grid}
          className={inputClassName}
        >
          {field.options.map((option, index) => (
            <RadioCard.Card
              key={index}
              value={option.value}
              checked={field.value === option.value}
              required={field.required}
              name={field.name}
            >
              {option.component && <option.component />}

              {option.label && <RadioCard.Label>{option.label}</RadioCard.Label>}

              {option.labelDescription && (
                <RadioCard.LabelDescription>{option.labelDescription}</RadioCard.LabelDescription>
              )}
            </RadioCard.Card>
          ))}
        </RadioCard.Group>
      );
    case 'radio':
      return (
        <Radio.Group name={field.name} className={inputClassName}>
          {field.options.map((option, index) => (
            <Radio.Wrapper key={index}>
              <Radio.Input
                value={option.value}
                required={field.required}
                name={field.name}
                id={`${field.name}-${option.value}`}
              />

              <Radio.Label htmlFor={`${field.name}-${option.value}`}>{option.label}</Radio.Label>
            </Radio.Wrapper>
          ))}
        </Radio.Group>
      );
    case 'checkbox':
      return (
        <Checkbox.Wrapper>
          <Checkbox
            {...field}
            name={field.name}
            id={field.name}
            className={clsx(field.className, inputClassName)}
          />
          <Checkbox.Label htmlFor={field.name} tooltip={field.tooltip}>
            {field.label}
          </Checkbox.Label>
        </Checkbox.Wrapper>
      );
    case 'select':
      return (
        <Select
          {...field}
          name={field.name}
          id={field.name}
          className={clsx(field.className, inputClassName)}
        >
          {field.options.map((option, index) => (
            <option
              key={index}
              value={option.value}
              disabled={option.disabled}
              selected={option.selected}
            >
              {option.label}
            </option>
          ))}
        </Select>
      );
    default: {
      const FieldComponent = getFieldComponentByType(field.type);

      const { hidePlaceHolder, ...rest } = field;

      return React.createElement(FieldComponent, {
        ...rest,
        name: rest.name,
        id: rest.id || rest.name,
        placeholder: hidePlaceHolder ? '' : rest.placeholder || rest.label,
        defaultValue: rest.defaultValue,
        inputClassName: inputClassName,
      });
    }
  }
}

const Form = ({
  initialValues,
  fields = [],
  formSubmitDirection = 'row',
  formSubmitAlign = 'start',
  submitButton = {
    label: 'Save',
    appearance: 'primary',
    size: 'base',
    disabled: false,
    arrowRight: false,
  },
  cancelButton = null,
  inputClassName = '',
  onSubmit,
  formSubmit = null,
  showState = true,
  children,
  onValuesChange = () => null,
  onDirty = () => null,
  validationSchema = null,
  autosave = false,
  ...props
}) => {
  const formIdRef = useRef(`form-${Math.random().toString(36).substr(2, 9)}`);

  return (
    <Formik
      {...props}
      initialValues={initialValues}
      onSubmit={async (values, actions) => {
        actions.setStatus(undefined);

        const { data, error, onSuccess, onError } = await onSubmit(values, actions);

        if (data) {
          actions.setStatus('');

          if (onSuccess) {
            onSuccess(data);
          }

          return true;
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }

          if (onError) {
            onError(error);
          }

          return false;
        }
      }}
      validate={values => {
        onValuesChange(values);
      }}
      validationSchema={validationSchema}
      validateOnBlur={props.validateOnBlur === true ? true : autosave ? true : false}
      validateOnChange={props.validateOnChange === true ? true : autosave ? true : false}
    >
      {({ dirty, status, values, setFieldValue, handleSubmit, errors, touched }) => {
        onDirty(dirty);

        const fieldsArray =
          typeof fields === 'function' ? fields({ values, setFieldValue, touched }) : fields;

        // if there are errors that are not in the fields, show the first one
        const errorsWithoutField = Object.entries(errors)
          .filter(([key]) => !fieldsArray?.find(field => field.name === key))
          .map(([, value]) => value);

        const firstError = errorsWithoutField.length ? errorsWithoutField[0] : undefined;

        return (
          <FormikForm>
            {renderFields({ fields, values, setFieldValue, touched }).map(field => (
              <Field
                key={field.name}
                field={{
                  ...field,
                  visible: field.visible === undefined ? true : field.visible,
                }}
                values={values}
                formId={formIdRef.current}
                inputClassName={inputClassName}
              />
            ))}

            {children}

            {status && typeof status === 'string' && <FormErrorBase>{status}</FormErrorBase>}
            {firstError && typeof firstError === 'string' && (
              <FormErrorBase>{firstError}</FormErrorBase>
            )}

            {autosave && <AutoSave showState={showState} />}

            {formSubmit
              ? typeof formSubmit === 'function'
                ? formSubmit({
                    dirty,
                    handleSubmit,
                  })
                : formSubmit
              : null}

            {!autosave && !formSubmit && (
              <FormSubmit
                isInline={!showState}
                align={formSubmitAlign}
                direction={formSubmitDirection}
                hasMargin={fields.length > 0 || status || firstError}
              >
                {formSubmitAlign === 'end' && (
                  <>
                    {showState && <FormSubmit.State visible={!!dirty} />}

                    {cancelButton && (
                      <FormSubmit.Cancel
                        appearance={cancelButton.appearance}
                        size={cancelButton.size}
                        onClick={cancelButton.onClick}
                      >
                        {cancelButton.label}
                      </FormSubmit.Cancel>
                    )}

                    <FormSubmit.Button
                      appearance={submitButton.appearance}
                      size={submitButton.size}
                      disabled={submitButton.disabled}
                      arrowRight={submitButton.arrowRight}
                    >
                      {submitButton.label}
                    </FormSubmit.Button>
                  </>
                )}

                {formSubmitAlign === 'start' && (
                  <>
                    {cancelButton && (
                      <FormSubmit.Cancel
                        appearance={cancelButton.appearance}
                        size={cancelButton.size}
                        onClick={cancelButton.onClick}
                      >
                        {cancelButton.label}
                      </FormSubmit.Cancel>
                    )}

                    <FormSubmit.Button
                      appearance={submitButton.appearance}
                      size={submitButton.size}
                      disabled={submitButton.disabled}
                      arrowRight={submitButton.arrowRight}
                    >
                      {submitButton.label}
                    </FormSubmit.Button>

                    {showState && <FormSubmit.State visible={!!dirty} />}
                  </>
                )}
              </FormSubmit>
            )}
          </FormikForm>
        );
      }}
    </Formik>
  );
};

export function renderFields({ fields, values, setFieldValue, touched }) {
  if (typeof fields === 'function') {
    return fields({ values, setFieldValue, touched });
  }

  return fields;
}

/**
 * Returns true if the field should be hidden
 * field can have a hidden or a visible property
 * both can be boolean or functions
 * @param {*} field
 * @param {*} values
 */
function getIsFieldHidden(field, values) {
  if (field.hidden === false) {
    return false;
  }

  if (field.hidden === true) {
    return true;
  }

  if (typeof field.hidden === 'function') {
    return field.hidden(values);
  }

  if (field.visible === false) {
    return true;
  }

  if (field.visible === true) {
    return false;
  }

  if (typeof field.visible === 'function') {
    return !field.visible(values);
  }

  return false;
}

export function Field({ field, values, inputClassName }) {
  const showLabel =
    !!field.label &&
    field.type !== 'checkbox' &&
    field.type !== 'toggle' &&
    field.type !== 'disclosure' &&
    field.type !== 'label';

  const isFieldHidden = getIsFieldHidden(field, values);

  const isReduceMargin =
    typeof field.reduceMargin === 'function' ? field.reduceMargin(values) : field.reduceMargin;

  return (
    <FormElement
      key={field.name}
      className={clsx({
        '!mb-0': field.noMargin,
        '!mb-3': isReduceMargin,
        'ml-[26px]': field.inset,
        hidden: isFieldHidden,
        '!mb-8': field.increaseMargin,
        relative: field.isRelative,
      })}
    >
      {showLabel && (
        <Label
          htmlFor={field.id || field.name}
          optional={field.optional && !field.hideOptional}
          tooltip={field.tooltip}
          tags={field.tags}
          description={field.labelDescription}
        >
          {field.label}
        </Label>
      )}

      <FormField field={field} inputClassName={inputClassName} />

      {field.additionalContent && field.additionalContent}

      {field.name && <FormError {...field} />}
    </FormElement>
  );
}

export default Form;
